import { useState, type FormEvent } from 'react';
import { login, ApiClientError } from '../../lib/api/client.js';

export function LoginPage({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      await onSignedIn();
    } catch (err) {
      // The server deliberately does not distinguish "no such account" from
      // "wrong password" (doc 06 §2.1), and neither does this.
      setError(
        err instanceof ApiClientError && err.status === 503
          ? 'The sign-in service is temporarily unavailable. Please try again shortly.'
          : err instanceof Error ? err.message : 'Sign in failed',
      );
      setBusy(false);
    }
  };

  return (
    <div className="arc-login">
      <div className="arc-login-card">
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div
            className="arc-brand-mark"
            style={{ width: 44, height: 44, margin: '0 auto 12px', fontSize: 19, borderRadius: 11 }}
            aria-hidden="true"
          >
            A
          </div>
          <h1 className="arc-h1">Sign in to ArcNet</h1>
          <p className="arc-muted" style={{ marginTop: 5, fontSize: 13.5 }}>
            Cloud communications platform
          </p>
        </div>

        <form className="arc-card arc-card-body" onSubmit={submit} noValidate>
          {error && (
            <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }} role="alert">
              <span aria-hidden="true">⚠</span>
              <span>{error}</span>
            </div>
          )}

          <div className="arc-field">
            <label className="arc-label" htmlFor="email">Email address</label>
            <input
              id="email" type="email" className="arc-input"
              autoComplete="username" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error !== null}
            />
          </div>

          <div className="arc-field">
            <label className="arc-label" htmlFor="password">Password</label>
            <input
              id="password" type="password" className="arc-input"
              autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error !== null}
            />
          </div>

          <button
            type="submit"
            className="arc-btn arc-btn-primary"
            style={{ width: '100%', marginTop: 4 }}
            disabled={busy || !email || !password}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="arc-subtle" style={{ textAlign: 'center', marginTop: 16, fontSize: 12 }}>
          Protected by rate limiting and audit logging
        </p>
      </div>
    </div>
  );
}
